/**
 * Load co-op family registration fee from public site settings.
 */

import { useEffect, useState } from 'react';

import { usePublicSiteSettings } from '@contexts/PublicSiteSettingsContext';
import { siteSettingsApi } from '@services/siteSettingsApi';
import {
  DEFAULT_FAMILY_REGISTRATION_FEE_CENTS,
  familyRegistrationAmountDueCents,
  familyRegistrationFeeApplyDiscountDescription,
  familyRegistrationFeeHowToJoinDescription,
  familyRegistrationFeeIntroLine,
  familyRegistrationFeeMarkPaidDescription,
  familyRegistrationFeeRemoveDiscountDescription,
  formatFamilyRegistrationFeeUsd,
  normalizeFamilyRegistrationFeeCents,
} from '@utils/familyRegistrationFee';

export function useFamilyRegistrationFee(): {
  feeCents: number;
  feeFormatted: string;
  feeIntroLine: string;
  feeHowToJoinDescription: string;
  markPaidDescription: (parentName: string, familyName?: string, discountCents?: number) => string;
  applyDiscountDescription: (parentName: string, familyName?: string) => string;
  removeDiscountDescription: (parentName: string, familyName?: string) => string;
  loading: boolean;
} {
  const { settings, loading: settingsLoading } = usePublicSiteSettings();
  const [feeCents, setFeeCents] = useState(() =>
    normalizeFamilyRegistrationFeeCents(settings?.familyRegistrationFeeCents)
  );
  // Without shared settings (and once they have stopped loading) this hook fetches its own copy.
  const [loading, setLoading] = useState(settings === null);

  // Follow the shared settings as they change (adjust state during render); the effect below
  // only runs the fallback fetch.
  const [prevShared, setPrevShared] = useState({ settings, settingsLoading });
  if (prevShared.settings !== settings || prevShared.settingsLoading !== settingsLoading) {
    setPrevShared({ settings, settingsLoading });
    if (settings) {
      setFeeCents(normalizeFamilyRegistrationFeeCents(settings.familyRegistrationFeeCents));
      setLoading(false);
    } else if (!settingsLoading) {
      setLoading(true);
    }
  }

  useEffect(() => {
    if (settings || settingsLoading) return;

    let cancelled = false;
    siteSettingsApi
      .get(null)
      .then((res) => {
        if (cancelled) return;
        setFeeCents(
          normalizeFamilyRegistrationFeeCents(res.data?.settings?.familyRegistrationFeeCents)
        );
      })
      .catch(() => {
        if (!cancelled) setFeeCents(DEFAULT_FAMILY_REGISTRATION_FEE_CENTS);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [settings, settingsLoading]);

  const feeFormatted = formatFamilyRegistrationFeeUsd(feeCents);

  return {
    feeCents,
    feeFormatted,
    feeIntroLine: familyRegistrationFeeIntroLine(feeCents),
    feeHowToJoinDescription: familyRegistrationFeeHowToJoinDescription(feeCents),
    markPaidDescription: (parentName: string, familyName?: string, discountCents = 0) => {
      const remainingCents = familyRegistrationAmountDueCents(feeCents, discountCents);
      const remainingFormatted =
        discountCents > 0 ? formatFamilyRegistrationFeeUsd(remainingCents) : undefined;
      return familyRegistrationFeeMarkPaidDescription(
        feeFormatted,
        parentName,
        familyName,
        remainingFormatted
      );
    },
    applyDiscountDescription: (parentName: string, familyName?: string) =>
      familyRegistrationFeeApplyDiscountDescription(feeFormatted, parentName, familyName),
    removeDiscountDescription: (parentName: string, familyName?: string) =>
      familyRegistrationFeeRemoveDiscountDescription(parentName, familyName),
    loading,
  };
}
